/**
 * /igaming/payment-hold — operator UI for Feature #157 (compliance payment hold).
 *
 * Layout (single-page, composition):
 *   • Summary strip — 4 counts (pending, approved, rejected, escalated).
 *   • Queue card — list of pending-review holds with filters (flag, affiliate,
 *     status). Click through to the detail drawer.
 *   • Detail drawer — single hold: metadata, decision buttons (approve,
 *     reject, escalate), reviewer + decision-note fields, audit trail.
 *
 * Data layer: TanStack Query + Sonner toasts.
 * Brand: emerald→sky headline gradient, zinc borders, 6–8px rounded, light
 * theme only. No emojis. No competitor names.
 */
import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	approveHold,
	canTransition,
	escalateHold,
	fetchHold,
	fetchHoldEvents,
	fetchStats,
	FLAG_LABELS,
	formatDate,
	formatDateTime,
	formatInteger,
	formatMoneyCents,
	HOLD_FLAGS,
	HOLD_STATUSES,
	igamingPaymentHoldQueryKeys,
	isTerminal,
	listHolds,
	rejectHold,
	STATUS_LABELS,
	type HoldFlag,
	type HoldStatus,
	type PaymentHold,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const TEXTAREA = `${INPUT} resize-none`;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-600 bg-red-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-red-700 hover:border-red-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SKY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-sky-700 hover:border-sky-700 disabled:cursor-not-allowed disabled:opacity-60";

function statusPill(status: HoldStatus): string {
	switch (status) {
		case "approved":
			return "inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200";
		case "rejected":
			return "inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 border border-red-200";
		case "escalated":
			return "inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200";
		case "pending_review":
		default:
			return "inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 border border-sky-200";
	}
}

const FLAG_PILL =
	"inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 border border-zinc-200";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IgamingPaymentHoldPageProps {
	initialHoldId?: string;
}

export function IgamingPaymentHoldPage(
	props: IgamingPaymentHoldPageProps = {},
) {
	const queryClient = useQueryClient();

	// ----- Filters -----
	const [filterStatus, setFilterStatus] = useState<HoldStatus | "">(
		"pending_review",
	);
	const [filterFlag, setFilterFlag] = useState<HoldFlag | "">("");
	const [filterAffiliate, setFilterAffiliate] = useState<string>("");
	const [committedAffiliate, setCommittedAffiliate] = useState<string>("");

	// ----- Selection -----
	const [selectedId, setSelectedId] = useState<string | null>(
		props.initialHoldId ?? null,
	);

	// ----- Decision form -----
	const [reviewedBy, setReviewedBy] = useState<string>("");
	const [decisionNote, setDecisionNote] = useState<string>("");

	// ----- Queries -----
	const statsQuery = useQuery({
		queryKey: igamingPaymentHoldQueryKeys.stats(),
		queryFn: fetchStats,
	});

	const listArgs = useMemo(
		() => ({
			status: filterStatus || undefined,
			flag: filterFlag || undefined,
			affiliate_id: committedAffiliate || undefined,
			limit: 100,
		}),
		[filterStatus, filterFlag, committedAffiliate],
	);

	const listQuery = useQuery({
		queryKey: igamingPaymentHoldQueryKeys.list(listArgs),
		queryFn: () => listHolds(listArgs),
	});

	const detailQuery = useQuery({
		queryKey: selectedId
			? igamingPaymentHoldQueryKeys.byId(selectedId)
			: igamingPaymentHoldQueryKeys.byId("_none"),
		queryFn: () => fetchHold(selectedId as string),
		enabled: selectedId !== null,
	});

	const eventsQuery = useQuery({
		queryKey: selectedId
			? igamingPaymentHoldQueryKeys.events(selectedId)
			: igamingPaymentHoldQueryKeys.events("_none"),
		queryFn: () => fetchHoldEvents(selectedId as string),
		enabled: selectedId !== null,
	});

	// ----- Mutations -----
	function invalidateAfterDecision(holdId: string) {
		queryClient.invalidateQueries({
			queryKey: igamingPaymentHoldQueryKeys.all,
		});
		queryClient.invalidateQueries({
			queryKey: igamingPaymentHoldQueryKeys.byId(holdId),
		});
		queryClient.invalidateQueries({
			queryKey: igamingPaymentHoldQueryKeys.events(holdId),
		});
	}

	const approveMutation = useMutation({
		mutationFn: approveHold,
		onSuccess: (resp) => {
			toast.success(
				resp.payout_blocked
					? "Hold approved — payout still blocked."
					: "Hold approved — payout released.",
			);
			invalidateAfterDecision(resp.hold.id);
			setDecisionNote("");
		},
		onError: (err: Error) => toast.error(err.message || "Approve failed."),
	});

	const rejectMutation = useMutation({
		mutationFn: rejectHold,
		onSuccess: (resp) => {
			toast.success("Hold rejected — payout blocked permanently.");
			invalidateAfterDecision(resp.hold.id);
			setDecisionNote("");
		},
		onError: (err: Error) => toast.error(err.message || "Reject failed."),
	});

	const escalateMutation = useMutation({
		mutationFn: escalateHold,
		onSuccess: (resp) => {
			toast.success("Hold escalated to risk desk.");
			invalidateAfterDecision(resp.hold.id);
			setDecisionNote("");
		},
		onError: (err: Error) => toast.error(err.message || "Escalate failed."),
	});

	const stats = statsQuery.data;
	const list = listQuery.data;
	const detail: PaymentHold | undefined = detailQuery.data;
	const events = eventsQuery.data?.events ?? [];

	const busy =
		approveMutation.isPending ||
		rejectMutation.isPending ||
		escalateMutation.isPending;

	const canApprove = !!detail && canTransition(detail.status, "approved");
	const canReject = !!detail && canTransition(detail.status, "rejected");
	const canEscalate = !!detail && canTransition(detail.status, "escalated");

	// ----- Handlers -----
	const handleCommitAffiliate = useCallback(() => {
		setCommittedAffiliate(filterAffiliate.trim());
	}, [filterAffiliate]);

	const handleDecide = useCallback(
		(action: "approve" | "reject" | "escalate") => {
			if (!detail || busy) return;
			const args = {
				id: detail.id,
				reviewed_by: reviewedBy.trim() || null,
				decision_note: decisionNote.trim() || null,
			};
			if (action === "approve") approveMutation.mutate(args);
			else if (action === "reject") rejectMutation.mutate(args);
			else escalateMutation.mutate(args);
		},
		[
			detail,
			busy,
			reviewedBy,
			decisionNote,
			approveMutation,
			rejectMutation,
			escalateMutation,
		],
	);

	return (
		<div
			data-testid="igaming-payment-hold-page"
			className="flex flex-col gap-6"
		>
			{/* Heading */}
			<header className="flex flex-col gap-1">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold text-transparent">
					iGaming payment holds
				</h1>
				<p className="text-sm text-zinc-600">
					Freeze individual iGaming conversions pending compliance review.
					Decisions are audited — approve releases the payout, reject blocks
					it permanently, escalate routes to the risk desk.
				</p>
			</header>

			{/* Summary strip */}
			<section
				className="grid grid-cols-2 gap-3 sm:grid-cols-4"
				aria-label="Hold status summary"
			>
				<SummaryTile
					label="Pending review"
					value={stats?.by_status.pending_review ?? 0}
					tone="sky"
				/>
				<SummaryTile
					label="Approved"
					value={stats?.by_status.approved ?? 0}
					tone="emerald"
				/>
				<SummaryTile
					label="Rejected"
					value={stats?.by_status.rejected ?? 0}
					tone="red"
				/>
				<SummaryTile
					label="Escalated"
					value={stats?.by_status.escalated ?? 0}
					tone="amber"
				/>
			</section>

			{/* Filters + queue */}
			<section
				className={`${CARD} flex flex-col gap-3 p-4`}
				aria-label="Hold queue"
			>
				<div className="flex items-center justify-between gap-2">
					<h2 className="text-base font-semibold text-zinc-900">Queue</h2>
					<span className="text-sm text-zinc-500">
						{formatInteger(list?.count ?? 0)} hold
						{(list?.count ?? 0) === 1 ? "" : "s"} shown
					</span>
				</div>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="igaming-hold-status">
							Status
						</label>
						<select
							id="igaming-hold-status"
							className={INPUT}
							value={filterStatus}
							onChange={(e) =>
								setFilterStatus(e.target.value as HoldStatus | "")
							}
						>
							<option value="">Any</option>
							{HOLD_STATUSES.map((s) => (
								<option key={s} value={s}>
									{STATUS_LABELS[s]}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="igaming-hold-flag">
							Compliance flag
						</label>
						<select
							id="igaming-hold-flag"
							className={INPUT}
							value={filterFlag}
							onChange={(e) =>
								setFilterFlag(e.target.value as HoldFlag | "")
							}
						>
							<option value="">Any</option>
							{HOLD_FLAGS.map((f) => (
								<option key={f} value={f}>
									{FLAG_LABELS[f]}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="igaming-hold-affiliate">
							Affiliate id
						</label>
						<div className="flex gap-2">
							<input
								id="igaming-hold-affiliate"
								className={INPUT}
								type="text"
								placeholder="aff_123"
								value={filterAffiliate}
								onChange={(e) => setFilterAffiliate(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										handleCommitAffiliate();
									}
								}}
								onBlur={handleCommitAffiliate}
							/>
							<button
								type="button"
								className={BTN_NEUTRAL}
								onClick={handleCommitAffiliate}
							>
								Filter
							</button>
						</div>
					</div>
				</div>

				{listQuery.isLoading && (
					<div className="text-sm text-zinc-500">Loading queue…</div>
				)}
				{listQuery.isError && (
					<div className="text-sm text-red-600">Failed to load holds.</div>
				)}

				{!listQuery.isLoading && (list?.count ?? 0) === 0 && (
					<div className="rounded-[6px] border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center text-sm text-zinc-600">
						No holds match the current filters.
					</div>
				)}

				{list && list.count > 0 && (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50">
								<tr>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Conversion
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Affiliate
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Flag
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Status
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Flagged
									</th>
									<th className="px-3 py-2 text-right font-medium text-zinc-600">
										Payout
									</th>
									<th className="px-3 py-2 text-right font-medium text-zinc-600">
										{"\u00A0"}
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{list.holds.map((row) => (
									<tr
										key={row.id}
										data-testid="queue-row"
										className={
											selectedId === row.id
												? "bg-emerald-50/40"
												: "hover:bg-zinc-50"
										}
									>
										<td className="px-3 py-2 font-mono text-xs text-zinc-900">
											{row.conversion_id}
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.affiliate_id}
										</td>
										<td className="px-3 py-2">
											<span className={FLAG_PILL}>
												{FLAG_LABELS[row.flag]}
											</span>
										</td>
										<td className="px-3 py-2">
											<span className={statusPill(row.status)}>
												{STATUS_LABELS[row.status]}
											</span>
										</td>
										<td className="px-3 py-2 text-zinc-700">
											{formatDate(row.created_at)}
										</td>
										<td className="px-3 py-2 text-right font-medium text-zinc-900">
											{formatMoneyCents(row.payout_cents, row.currency)}
										</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												className={BTN_NEUTRAL}
												onClick={() => setSelectedId(row.id)}
												data-testid="queue-row-review"
											>
												Review
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{/* Detail drawer */}
			{selectedId && (
				<section
					className={`${CARD} flex flex-col gap-4 p-4`}
					aria-label="Hold detail"
					data-testid="hold-detail"
				>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<h2 className="text-base font-semibold text-zinc-900">
							Hold <span className="font-mono">{selectedId}</span>
						</h2>
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={() => setSelectedId(null)}
						>
							Close
						</button>
					</div>

					{detailQuery.isLoading && (
						<div className="text-sm text-zinc-500">Loading hold…</div>
					)}
					{detailQuery.isError && (
						<div className="text-sm text-red-600">
							Failed to load hold.
						</div>
					)}

					{detail && (
						<>
							<div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
								<MetaField label="Status">
									<span className={statusPill(detail.status)}>
										{STATUS_LABELS[detail.status]}
									</span>
								</MetaField>
								<MetaField label="Flag">
									<span className={FLAG_PILL}>
										{FLAG_LABELS[detail.flag]}
									</span>
								</MetaField>
								<MetaField label="Payout">
									<span className="font-medium text-zinc-900">
										{formatMoneyCents(detail.payout_cents, detail.currency)}
									</span>
								</MetaField>
								<MetaField label="Flagged">
									{formatDateTime(detail.created_at)}
								</MetaField>
								<MetaField label="Conversion">
									<span className="font-mono text-xs">
										{detail.conversion_id}
									</span>
								</MetaField>
								<MetaField label="Affiliate">
									<span className="font-mono text-xs">
										{detail.affiliate_id}
									</span>
								</MetaField>
								<MetaField label="Brand">
									{detail.brand_id ? (
										<span className="font-mono text-xs">
											{detail.brand_id}
										</span>
									) : (
										<span className="text-zinc-400">—</span>
									)}
								</MetaField>
								<MetaField label="Offer">
									{detail.offer_id ? (
										<span className="font-mono text-xs">
											{detail.offer_id}
										</span>
									) : (
										<span className="text-zinc-400">—</span>
									)}
								</MetaField>
							</div>

							{detail.reason && (
								<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700">
									<div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">
										Reason
									</div>
									<div>{detail.reason}</div>
								</div>
							)}

							{!isTerminal(detail.status) && (
								<div className="flex flex-col gap-3 border-t border-zinc-200 pt-4">
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
										<div className="flex flex-col gap-1">
											<label
												className={LABEL}
												htmlFor="igaming-hold-reviewer"
											>
												Reviewed by
											</label>
											<input
												id="igaming-hold-reviewer"
												type="text"
												className={INPUT}
												maxLength={128}
												placeholder="compliance@company.com"
												value={reviewedBy}
												onChange={(e) => setReviewedBy(e.target.value)}
												autoComplete="off"
											/>
										</div>
										<div className="flex flex-col gap-1">
											<label
												className={LABEL}
												htmlFor="igaming-hold-note"
											>
												Decision note
											</label>
											<textarea
												id="igaming-hold-note"
												className={TEXTAREA}
												rows={2}
												maxLength={1000}
												placeholder="e.g. docs received, AML cleared"
												value={decisionNote}
												onChange={(e) => setDecisionNote(e.target.value)}
											/>
										</div>
									</div>
									<div className="flex flex-wrap items-center justify-end gap-2">
										<button
											type="button"
											className={BTN_SKY}
											onClick={() => handleDecide("escalate")}
											disabled={!canEscalate || busy}
											data-testid="hold-escalate-btn"
										>
											{escalateMutation.isPending ? "Escalating…" : "Escalate"}
										</button>
										<button
											type="button"
											className={BTN_DANGER}
											onClick={() => handleDecide("reject")}
											disabled={!canReject || busy}
											data-testid="hold-reject-btn"
										>
											{rejectMutation.isPending ? "Rejecting…" : "Reject"}
										</button>
										<button
											type="button"
											className={BTN_PRIMARY}
											onClick={() => handleDecide("approve")}
											disabled={!canApprove || busy}
											data-testid="hold-approve-btn"
										>
											{approveMutation.isPending ? "Approving…" : "Approve"}
										</button>
									</div>
								</div>
							)}

							{isTerminal(detail.status) && (
								<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700">
									Decision is final.{" "}
									{detail.reviewed_by && (
										<>
											Reviewed by{" "}
											<span className="font-mono">{detail.reviewed_by}</span>{" "}
										</>
									)}
									{detail.reviewed_at && (
										<>on {formatDateTime(detail.reviewed_at)}.</>
									)}
								</div>
							)}

							{/* Audit trail */}
							<div className="flex flex-col gap-2 border-t border-zinc-200 pt-4">
								<h3 className="text-sm font-semibold text-zinc-900">
									Audit trail
								</h3>
								{eventsQuery.isLoading && (
									<div className="text-sm text-zinc-500">Loading events…</div>
								)}
								{events.length === 0 && !eventsQuery.isLoading && (
									<div className="text-sm text-zinc-500">No events yet.</div>
								)}
								{events.length > 0 && (
									<ol className="flex flex-col gap-2 text-sm">
										{events.map((ev) => (
											<li
												key={ev.id}
												className="flex flex-col gap-0.5 rounded-[6px] border border-zinc-200 bg-white px-3 py-2"
												data-testid="event-row"
											>
												<div className="flex flex-wrap items-center gap-2">
													<span className="text-xs font-semibold uppercase tracking-wide text-zinc-600">
														{ev.event_type}
													</span>
													{ev.from_status && ev.to_status && (
														<span className="text-xs text-zinc-500">
															{STATUS_LABELS[ev.from_status]} →{" "}
															{STATUS_LABELS[ev.to_status]}
														</span>
													)}
													<span className="ml-auto text-xs text-zinc-500">
														{formatDateTime(ev.created_at)}
													</span>
												</div>
												{(ev.actor || ev.note) && (
													<div className="text-xs text-zinc-600">
														{ev.actor && (
															<span className="font-mono">{ev.actor}</span>
														)}
														{ev.actor && ev.note ? " · " : ""}
														{ev.note}
													</div>
												)}
											</li>
										))}
									</ol>
								)}
							</div>
						</>
					)}
				</section>
			)}
		</div>
	);
}

export default IgamingPaymentHoldPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryTile(props: {
	label: string;
	value: number;
	tone: "sky" | "emerald" | "red" | "amber";
}) {
	const ring =
		props.tone === "emerald"
			? "border-emerald-200"
			: props.tone === "red"
				? "border-red-200"
				: props.tone === "amber"
					? "border-amber-200"
					: "border-sky-200";
	const text =
		props.tone === "emerald"
			? "text-emerald-700"
			: props.tone === "red"
				? "text-red-700"
				: props.tone === "amber"
					? "text-amber-700"
					: "text-sky-700";
	return (
		<div
			className={`rounded-[8px] border bg-white p-3 ${ring}`}
			data-testid="summary-tile"
		>
			<div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{props.label}
			</div>
			<div className={`mt-1 text-2xl font-semibold ${text}`}>
				{formatInteger(props.value)}
			</div>
		</div>
	);
}

function MetaField(props: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-0.5">
			<div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{props.label}
			</div>
			<div>{props.children}</div>
		</div>
	);
}
